import { type GenericId as Id } from "convex/values";

// Mode types for explicit app and browser mode management
export type AppMode = "whitelist" | "blacklist" | "categories";
export type BrowserMode = "none" | "categories" | "whitelist";

// Convex ID types for all tables
export type ConvexDeviceId = Id<"devices">;
export type ConvexLevelId = Id<"levels">;
export type ConvexRuleId = Id<"rules">;
export type ConvexAppId = Id<"apps">;
export type ConvexUserId = Id<"users">;
export type ConvexUserRoleId = Id<"userRoles">;
export type ConvexGroupId = Id<"groups">;
export type ConvexDeviceConfigId = Id<"deviceConfigs">;

// Type-safe ID conversion utilities  
export const toConvexId = {
  device: (id: string): ConvexDeviceId => id as ConvexDeviceId,
  level: (id: string): ConvexLevelId => id as ConvexLevelId,
  rule: (id: string): ConvexRuleId => id as ConvexRuleId,
  app: (id: string): ConvexAppId => id as ConvexAppId,
  user: (id: string): ConvexUserId => id as ConvexUserId,
  userRole: (id: string): ConvexUserRoleId => id as ConvexUserRoleId,
  group: (id: string): ConvexGroupId => id as ConvexGroupId,
  deviceConfig: (id: string): ConvexDeviceConfigId => id as ConvexDeviceConfigId,
};

// API Response types based on actual API definitions
export interface ConvexLevel {
  _creationTime: number;
  _id: ConvexLevelId;
  allowedApps: Array<ConvexAppId>;
  allowedUrls?: Array<string>;
  appFilter: Record<string, true | {
    exclude?: { tags: Array<string>; type: "any" | "all" };
    include?: { tags: Array<string>; type: "any" | "all" };
  }>;
  blockedApps: Array<ConvexAppId>;
  blockedUrls?: Array<string>;
  description?: string;
  group?: ConvexGroupId;
  name: string;
  policyCategories: Array<string>;
  private: boolean;
  restrictions: Array<string>;
}

export interface ConvexDevice {
  _id: ConvexDeviceId;
  deviceConfig: ConvexDeviceConfigId;
  jssid?: number;
  serial?: string;
  user?: {
    email?: string;
    firstName: string;
    lastName: string;
    phone?: string;
  };
}

export interface ConvexApp {
  _creationTime: number;
  _id: ConvexAppId;
  bundleId: string;
  category: string;
  iconUrl: string;
  name: string;
  system: boolean;
  tags: Array<string>;
  trackId: string;
  webkit?: Array<string>;
}

export interface ConvexUser {
  roles: Array<
    | {
        _creationTime: number;
        _id: ConvexUserRoleId;
        group: ConvexGroupId;
        role: "groupAdmin" | "groupViewer";
        user: string;
      }
    | {
        _creationTime: number;
        _id: ConvexUserRoleId;
        device: ConvexDeviceId;
        role: "user";
        user: string;
      }
  >;
}

export interface ConvexDeviceDetails {
  adBlock: boolean;
  device: {
    deviceConfig: ConvexDeviceConfigId;
    jssid?: number;
    profile?: string;
    serial?: string;
  };
  level?: string;
  user?: {
    email?: string;
    firstName: string;
    lastName: string;
    phone?: string;
  };
}